import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { Button } from "antd";
import { PrinterOutlined, ArrowLeftOutlined } from "@ant-design/icons";
import "./mineralTesting.scss";
import { getSingleMineralReport } from "../../../modules/actions/mineralActions";
import logo from "../../../Images/Logo 2.png";

const DEFAULT_LIMITATIONS = `This report relates only to the sample(s) submitted to Galaxy Mineral Testing Laboratory and tested in accordance with the analytical methods stated herein. Unless explicitly stated, samples were not drawn by Galaxy Mineral Testing Laboratory. Results apply only to the sample(s) as received. No assurance is given that the tested sample(s) are representative of the entire lot, consignment, shipment, mineral deposit, or production batch. All analytical results are subject to normal analytical tolerances, instrumental limitations, and measurement uncertainty. This report is an analytical test report only and does not constitute a certificate of value, origin, ownership, recoverability, or commercial viability. This report is issued exclusively for the Client and shall not be reproduced, partially or in full, without written authorization from Galaxy Mineral Testing Laboratory. The Laboratory's liability, if any, is strictly limited in accordance with the Terms & Conditions printed on the reverse of this certificate.`;

const PRINT_STYLE = `
    @page { size: A4 portrait; margin: 12mm 14mm; }
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: Arial, sans-serif; font-size: 11px; color: #000; background: #fff; }

    /* ── Header ── */
    .mtr-header { display: flex; align-items: center; gap: 16px; margin-bottom: 10px; }
    .mtr-logo { width: 68px; height: 68px; object-fit: contain; flex-shrink: 0; }
    .mtr-header-text { display: flex; flex-direction: column; justify-content: center; }
    .mtr-lab-name { font-size: 20px; font-weight: 900; letter-spacing: 0.5px; color: #1a2a4a; text-transform: uppercase; line-height: 1.15; }
    .mtr-lab-sub { font-size: 9.5px; color: #333; margin-top: 5px; line-height: 1.5; }
    .mtr-report-title { text-align: center; font-size: 13px; font-weight: bold; letter-spacing: 0.3px; margin: 10px 0 12px; border-top: 2px solid #1a2a4a; border-bottom: 1px solid #1a2a4a; padding: 6px 0; }

    /* ── Info Grid ── */
    .mtr-info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 6px 20px; margin-bottom: 14px; }
    .mtr-info-row { display: flex; gap: 5px; padding: 3px 0; font-size: 11px; }
    .mtr-info-label { font-weight: bold; white-space: nowrap; }
    .mtr-info-val { color: #222; }
    .mtr-info-full { grid-column: 1 / -1; }
    .mtr-divider { border: none; border-top: 1px solid #aaa; margin: 10px 0; }

    /* ── Results & Analysis ── */
    .mtr-section-title { font-weight: bold; font-size: 11px; margin-bottom: 8px; }
    .mtr-analysis-para { font-size: 11px; line-height: 1.65; margin-bottom: 16px; text-align: justify; }

    /* ── Table ── */
    .mtr-table { width: 60%; border-collapse: collapse; margin: 0 auto 20px; }
    .mtr-table th, .mtr-table td { border: 1px solid #000; padding: 7px 10px; text-align: center; font-size: 11px; }
    .mtr-table th { background: #f5f5f5; font-weight: bold; }
    .mtr-table td:nth-child(2) { text-align: left; }

    /* ── Signature ── */
    .mtr-signature { margin: 40px 0 14px; }
    .mtr-sig-line { border-top: 1px solid #000; width: 180px; margin-bottom: 4px; }
    .mtr-sig-label { font-size: 11px; line-height: 1.6; }

    /* ── Remarks ── */
    .mtr-remarks-title { font-weight: bold; font-size: 11px; margin-bottom: 6px; }
    .mtr-remarks-body { font-size: 11px; line-height: 1.7; }
    .mtr-remark-line::before { content: "• "; }

    /* ── Limitations ── */
    .mtr-limitations { margin-top: 16px; padding-top: 10px; border-top: 1px solid #bbb; }
    .mtr-limitations-title { font-weight: bold; font-size: 11px; margin-bottom: 6px; }
    .mtr-limitations-body { font-size: 10px; line-height: 1.6; text-align: justify; }

    /* ── Footer ── */
    .mtr-footer { text-align: center; font-size: 9.5px; margin-top: 18px; padding-top: 8px; border-top: 1px solid #000; color: #333; line-height: 1.7; }
    .mtr-footer a { color: #1a6ab5; text-decoration: none; }
`;

function MineralTestingPrint(props) {
    const [reportData, setReportData] = useState(null);

    useEffect(() => {
        if (props.match && props.match.params && props.match.params.reportNo) {
            props.getSingleMineralReport(props.match.params.reportNo);
        }
    }, []);

    useEffect(() => {
        if (props.singleMineralReport) {
            setReportData(props.singleMineralReport);
        }
    }, [props.singleMineralReport]);

    const formatDate = (date) => {
        if (!date) return "";
        const d = new Date(date);
        const day = String(d.getDate()).padStart(2, "0");
        const month = String(d.getMonth() + 1).padStart(2, "0");
        return `${day}/${month}/${d.getFullYear()}`;
    };

    const printElem = () => {
        const content = document.getElementById("mineralReportPrint").innerHTML;
        const logoSrc = document.querySelector("#mineralReportPrint .mtr-logo")
            ? document.querySelector("#mineralReportPrint .mtr-logo").src
            : "";
        const win = window.open("", "_blank");
        win.document.write(`<!DOCTYPE html><html><head>
            <meta charset="UTF-8"/>
            <title>Mineral Testing Report - ${(reportData && reportData.reportNo) || ""}</title>
            <style>${PRINT_STYLE}</style>
        </head><body>${content}</body></html>`);
        win.document.close();
        setTimeout(() => { win.focus(); win.print(); }, 600);
    };

    if (!reportData) {
        return (
            <div className="mineralTestingPrint">
                <div className="loadingDiv">Loading...</div>
            </div>
        );
    }

    const remarks = (reportData.remarks || "").split("\n").filter(Boolean);

    return (
        <div className="mineralTestingPrint">
            <div className="printHeader">
                <Button icon={<ArrowLeftOutlined />} onClick={() => props.history.push("/mineral-testing")}>
                    Back
                </Button>
                <Button type="primary" icon={<PrinterOutlined />} onClick={printElem} style={{ marginLeft: 10 }}>
                    Print Report
                </Button>
            </div>

            <div id="mineralReportPrint" className="mtr-page">
                <style>{PRINT_STYLE}</style>

                {/* ── Header ── */}
                <div className="mtr-header">
                    <img src={logo} alt="GGI Logo" className="mtr-logo" />
                    <div className="mtr-header-text">
                        <div className="mtr-lab-name">Galaxy Mineral Testing Laboratory</div>
                        {/* <div className="mtr-lab-sub">
                            Ground Floor, Sir Jahangir Kothari Trust Building, Shahrah-e-Iraq, Saddar, Karachi.&nbsp;&nbsp;|&nbsp;&nbsp;
                            Anwaar Flats, 2nd Floor, Shah Qabool Colony 2, Namak Mandi, Peshawar.
                        </div> */}
                    </div>
                </div>

                <div className="mtr-report-title">REPORT OF ANALYSIS – MINERAL TESTING</div>

                {/* ── Info Grid ── */}
                <div className="mtr-info-grid">
                    <div className="mtr-info-row">
                        <span className="mtr-info-label">Report No.:</span>
                        <span className="mtr-info-val">{reportData.reportNo}</span>
                    </div>
                    <div className="mtr-info-row">
                        <span className="mtr-info-label">Client:</span>
                        <span className="mtr-info-val">{reportData.client}</span>
                    </div>
                    <div className="mtr-info-row">
                        <span className="mtr-info-label">Sample Description:</span>
                        <span className="mtr-info-val">{reportData.sampleDescription}</span>
                    </div>
                    <div className="mtr-info-row">
                        <span className="mtr-info-label">Sample Ref / Lot No.:</span>
                        <span className="mtr-info-val">{reportData.sampleRefLotNo}</span>
                    </div>
                    <div className="mtr-info-row">
                        <span className="mtr-info-label">Sample Weight:</span>
                        <span className="mtr-info-val">{reportData.sampleWeight} g</span>
                    </div>
                    <div className="mtr-info-row">
                        <span className="mtr-info-label">Date of Analysis:</span>
                        <span className="mtr-info-val">{formatDate(reportData.dateOfAnalysis)}</span>
                    </div>
                    <div className="mtr-info-row">
                        <span className="mtr-info-label">Date of Receipt:</span>
                        <span className="mtr-info-val">{formatDate(reportData.dateOfReceipt)}</span>
                    </div>
                    <div className="mtr-info-row">
                        <span className="mtr-info-label">Nature of Testing:</span>
                        <span className="mtr-info-val">{reportData.natureOfTesting}</span>
                    </div>
                </div>

                <hr className="mtr-divider" />

                {/* ── Results & Analysis ── */}
                <div className="mtr-section-title">RESULTS &amp; ANALYSIS</div>
                <p className="mtr-analysis-para">
                    The submitted mineral sample was analyzed using destructive testing methods, which may include sample
                    preparation, crushing, powdering, dissolution, and instrumental or chemical analysis. Analytical techniques
                    may include, but are not limited to, XRF, XRD, ICP, AAS, wet chemical analysis, and other laboratory
                    methods as deemed appropriate. The results reported are based on the portion of the sample consumed during testing.
                </p>

                {/* ── Table ── */}
                <table className="mtr-table">
                    <thead>
                        <tr>
                            <th>Sr. No.</th>
                            <th>Component / Element</th>
                            <th>Result</th>
                        </tr>
                    </thead>
                    <tbody>
                        {(reportData.resultsData || []).map((row, index) => (
                            <tr key={index}>
                                <td>{row.srNo}</td>
                                <td>{row.component}</td>
                                <td>{row.result}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>

                {/* ── Signature ── */}
                <div className="mtr-signature">
                    <div className="mtr-sig-line"></div>
                    <div className="mtr-sig-label">Authorized Signatory</div>
                    <div className="mtr-sig-label">Galaxy Mineral Testing Laboratory</div>
                    {reportData.authorizedSignatory && (
                        <div className="mtr-sig-label" style={{ marginTop: 3 }}>{reportData.authorizedSignatory}</div>
                    )}
                </div>

                {/* ── Remarks ── */}
                <div className="mtr-remarks-title">REMARKS</div>
                <div className="mtr-remarks-body">
                    {remarks.length > 0
                        ? remarks.map((line, i) => <div key={i} className="mtr-remark-line">{line}</div>)
                        : (
                            <>
                                <div className="mtr-remark-line">The reported results represent the analyzed portion of the submitted sample only.</div>
                                <div className="mtr-remark-line">Due to sample heterogeneity, results may vary if a different portion of the sample is tested.</div>
                                <div className="mtr-remark-line">The sample was partially or fully consumed during analysis.</div>
                            </>
                        )
                    }
                </div>

                {/* ── Limitations ── */}
                <div className="mtr-limitations">
                    <div className="mtr-limitations-title">IMPORTANT LIMITATIONS &amp; NOTES</div>
                    <div className="mtr-limitations-body">{DEFAULT_LIMITATIONS}</div>
                </div>

                {/* ── Footer ── */}
                <div className="mtr-footer">
                    Ground Floor, Sir Jahangir Kothari Trust Building, Shahrah-e-Iraq, Saddar, Karachi.&nbsp;&nbsp;&nbsp;
                    Anwaar Flats, 2nd Floor, Shah Qabool Colony 2, Namak Mandi, Peshawar.<br />
                    <a href="http://www.ggilab.com">www.ggilab.com</a>&nbsp;&nbsp;&nbsp;
                    <a href="mailto:info@ggilab.com">info@ggilab.com</a>&nbsp;&nbsp;&nbsp;
                    +92-21-3521-6044&nbsp;&nbsp;&nbsp;+92-91-2568456
                </div>
            </div>
        </div>
    );
}

const mapStateToProps = (state) => {
    const mineralStore = state.mineralStore || {};
    return {
        singleMineralReport: mineralStore.singleMineralReport,
        mineralReportLoading: mineralStore.mineralReportLoading
    };
};

const mapDispatchToProps = (dispatch) => ({
    getSingleMineralReport: (reportNo) => dispatch(getSingleMineralReport(reportNo))
});

export default connect(mapStateToProps, mapDispatchToProps)(MineralTestingPrint);